import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { formatEuros, shortDate } from '../format';
import type { FinancesResponse, Obligation, Payment } from '../types';
import { PaymentForm } from './PaymentForm';
import { ProfitabilityPanel } from './ProfitabilityPanel';

const METHOD_LABELS: Record<string, string> = {
  VIREMENT: 'Virement', ESPECES: 'Espèces', CHEQUE: 'Chèque', AUTRE: 'Autre', STUDAPART: 'Studapart',
};

/** Envoie par e-mail une quittance déjà émise, avec le PDF en pièce jointe. */
function SendReceiptButton({ obligationId }: { obligationId: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  async function send(): Promise<void> {
    setState('busy');
    setError('');
    try {
      await api(`/api/admin/obligations/${obligationId}/receipt/send`, { method: 'POST' });
      setState('sent');
    } catch (caught) {
      setState('error');
      setError(caught instanceof Error ? caught.message : 'Envoi impossible.');
    }
  }

  if (state === 'sent') return <span className="muted">Envoyée ✓</span>;
  return (
    <span>
      <button type="button" className="text-button" disabled={state === 'busy'} onClick={() => void send()}>
        {state === 'busy' ? 'Envoi…' : 'Envoyer par e-mail'}
      </button>
      {state === 'error' && <span className="warn-text"> {error}</span>}
    </span>
  );
}

/**
 * Quittances délivrables. Un dépôt de garantie n'y figure jamais : ce n'est pas
 * un loyer, et le quittancer donnerait au locataire une preuve opposable au
 * bailleur au moment de la restitution. Le serveur refuse aussi, ce filtrage ne
 * fait qu'éviter de proposer un bouton qui échouerait.
 */
function ReceiptList({ obligations, issued, busy, onIssue }: {
  obligations: Obligation[];
  issued: Record<string, string>;
  busy: string;
  onIssue: (obligationId: string) => void;
}) {
  const eligible = obligations
    .filter((obligation) => obligation.kind !== 'SECURITY_DEPOSIT' && obligation.period)
    .sort((left, right) => (left.period ?? '').localeCompare(right.period ?? ''));
  if (eligible.length === 0) return null;
  return (
    <details className="item-history">
      <summary>Quittances et échéances ({eligible.length})</summary>
      <ul className="receipt-list">
      {eligible.map((obligation) => {
        const settled = obligation.paidCents >= obligation.amountCents;
        const documentId = issued[obligation.id];
        return (
          <li key={obligation.id}>
            <span>{obligation.period}</span>
            <span className="muted">{formatEuros(obligation.paidCents)} / {formatEuros(obligation.amountCents)}</span>
            {documentId
              ? (
                <span className="receipt-actions">
                  <a href={`/api/files/${documentId}`} target="_blank" rel="noreferrer">Quittance</a>
                  <SendReceiptButton obligationId={obligation.id} />
                </span>
              )
              : (
                <button
                  type="button"
                  className="text-button"
                  disabled={!settled || busy === obligation.id}
                  title={settled
                    ? undefined
                    : `Une quittance atteste d'un loyer payé en entier. Il manque `
                      + `${formatEuros(obligation.amountCents - obligation.paidCents)}.`}
                  onClick={() => onIssue(obligation.id)}
                >
                  {settled
                    ? 'Quittance'
                    : `il manque ${formatEuros(obligation.amountCents - obligation.paidCents)}`}
                </button>
              )}
          </li>
        );
      })}
      </ul>
    </details>
  );
}

export function FinancesPanel() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState<FinancesResponse | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [error, setError] = useState('');
  const [payingLeaseId, setPayingLeaseId] = useState('');
  // Quittances produites pendant la session, par échéance : le lien apparaît
  // sans recharger toute la page.
  const [receipts, setReceipts] = useState<Record<string, string>>({});
  const [busyReceipt, setBusyReceipt] = useState('');

  async function issueReceipt(obligationId: string): Promise<void> {
    setBusyReceipt(obligationId);
    setError('');
    try {
      const result = await api<{ documentId: string }>(`/api/admin/obligations/${obligationId}/receipt`, {
        method: 'POST',
      });
      setReceipts((current) => ({ ...current, [obligationId]: result.documentId }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Quittance impossible.');
    } finally {
      setBusyReceipt('');
    }
  }

  async function load(target = year): Promise<void> {
    try {
      const [finances, paid] = await Promise.all([
        api<FinancesResponse>(`/api/admin/finances?year=${target}`),
        api<{ payments: Payment[] }>(`/api/admin/payments?year=${target}`),
      ]);
      setData(finances);
      setPayments(paid.payments);
      setError('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Chargement impossible.');
    }
  }

  useEffect(() => { void load(); }, []);

  // Un versement se saisit par bail : c'est le locataire qui paie, et les
  // échéances affectables sont les siennes.
  const leases = useMemo(() => {
    const map = new Map<string, { id: string; roomName: string; tenantName: string; obligations: Obligation[] }>();
    for (const obligation of data?.obligations ?? []) {
      const entry = map.get(obligation.leaseId)
        ?? { id: obligation.leaseId, roomName: obligation.roomName, tenantName: obligation.tenantName, obligations: [] };
      entry.obligations.push(obligation);
      map.set(obligation.leaseId, entry);
    }
    return [...map.values()].sort((a, b) => a.roomName.localeCompare(b.roomName));
  }, [data]);

  const paying = leases.find((lease) => lease.id === payingLeaseId) ?? null;
  const years = [0, 1, 2].map((offset) => String(new Date().getFullYear() - offset));

  return (
    <section className="panel">
      <div className="section-heading">
        <div><p className="eyebrow">Suivi</p><h2>Finances {data?.year}</h2></div>
        <div className="finances-toolbar">
          <select value={year} onChange={(event) => { setYear(event.target.value); setPayingLeaseId(''); void load(event.target.value); }}>
            {years.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <a className="button secondary compact" href={`/api/admin/finances/export?year=${year}`}>Export comptable</a>
        </div>
      </div>

      {error && <div className="inline-message error" role="alert">{error}</div>}
      {!data && !error && <p className="muted">Chargement…</p>}

      {data && (
        <>
          {/* Le reste à percevoir en premier : c'est le seul de ces quatre
              chiffres qui appelle une action. */}
          <dl className="kpi-grid">
            <div className={data.balance.rentDueCents - data.balance.rentReceivedCents > 0 ? 'kpi alert' : 'kpi'}>
              <dt>Reste à percevoir</dt>
              <dd>{formatEuros(data.balance.rentDueCents - data.balance.rentReceivedCents)}</dd>
            </div>
            <div className="kpi">
              <dt>Loyers encaissés</dt>
              <dd>{formatEuros(data.balance.rentReceivedCents)}</dd>
              <p>sur {formatEuros(data.balance.rentDueCents)} attendus en {data.year}.</p>
            </div>
            <div className="kpi">
              <dt>Dépenses déductibles</dt>
              <dd>{formatEuros(data.balance.deductibleExpensesCents)}</dd>
              <p>Détail dans le livre comptable.</p>
            </div>
            <div className="kpi">
              <dt>Dépôts détenus</dt>
              <dd>{formatEuros(data.balance.depositsHeldCents)}</dd>
              <p>Somme due aux locataires, hors recettes.</p>
            </div>
          </dl>

          <div className="deposit-note">
            <strong>Dépôts de garantie détenus : {formatEuros(data.balance.depositsHeldCents)}</strong>
            <span>
              Ces sommes ne sont pas des recettes : elles sont détenues pour le compte des locataires et
              devront être restituées. Elles n'entrent pas dans les loyers encaissés ci-dessus.
              {data.balance.depositsDueCents > 0 && ` Reste à percevoir : ${formatEuros(data.balance.depositsDueCents)}.`}
            </span>
          </div>

          {paying && (
            <PaymentForm
              lease={paying}
              obligations={paying.obligations}
              onCancel={() => setPayingLeaseId('')}
              onDone={async () => { setPayingLeaseId(''); await load(); }}
            />
          )}

          <h3 className="subhead">Par bail</h3>
          <div className="record-list">
            {leases.length === 0 && (
              <div className="empty-state">
                Aucune échéance pour {data.year}. Générez un échéancier depuis la fiche d'un bail.
              </div>
            )}
            {leases.map((lease) => {
              const due = lease.obligations.reduce((total, obligation) => total + obligation.amountCents, 0);
              const paid = lease.obligations.reduce((total, obligation) => total + obligation.paidCents, 0);
              const late = lease.obligations.filter((obligation) => obligation.paidCents < obligation.amountCents).length;
              return (
                <article className="record-card" key={lease.id}>
                  <div className="record-heading">
                    <div>
                      <strong>{lease.roomName} · {lease.tenantName}</strong>
                      <span className="record-code">{lease.obligations.length} échéance(s), {late} non soldée(s)</span>
                    </div>
                    <button
                      className="button compact"
                      onClick={() => setPayingLeaseId(payingLeaseId === lease.id ? '' : lease.id)}
                      disabled={late === 0}
                    >
                      {late === 0 ? 'Tout est soldé' : 'Saisir un versement'}
                    </button>
                  </div>
                  <p className="record-line">
                    <span>{formatEuros(paid)} reçu sur {formatEuros(due)}</span>
                    <span className={paid >= due ? 'muted' : 'warn-text'}>
                      {paid >= due ? 'à jour' : `reste ${formatEuros(due - paid)}`}
                    </span>
                  </p>
                  <ReceiptList
                    obligations={lease.obligations}
                    issued={receipts}
                    busy={busyReceipt}
                    onIssue={(id) => void issueReceipt(id)}
                  />
                </article>
              );
            })}
          </div>

          {/* Rentabilité et historique des versements : consultés de loin en
              loin, ils n'ont pas à pousser le suivi des loyers hors de l'écran. */}
          <details className="access-panel">
            <summary>Rentabilité — indicateurs annuels</summary>
            <ProfitabilityPanel year={year} />
          </details>

          <details className="access-panel">
            <summary>Versements enregistrés ({payments.length})</summary>
          {payments.length === 0 && <div className="empty-state">Aucun versement saisi pour {data.year}.</div>}
          <div className="record-list">
            {payments.map((payment) => (
              <article className="record-card" key={payment.id}>
                <div className="record-heading">
                  <div>
                    <strong>{payment.roomName} · {payment.tenantName}</strong>
                    <span className="record-code">
                      {shortDate(payment.receivedOn)} · {METHOD_LABELS[payment.method] ?? payment.method}
                      {payment.reference ? ` · ${payment.reference}` : ''}
                    </span>
                  </div>
                  <strong>{formatEuros(payment.amountCents)}</strong>
                </div>
                {payment.allocatedCents < payment.amountCents && (
                  <p className="record-line warn-text">
                    {formatEuros(payment.amountCents - payment.allocatedCents)} non affecté à une échéance
                  </p>
                )}
              </article>
            ))}
          </div>
          </details>
        </>
      )}
    </section>
  );
}
